import type { ParsedBundleContent } from '@/lib/medcom/parseCareCommunicationBundle';
import { organizationFieldsFromStored } from '@/lib/medcom/sorEntityName';

type ThreadMessage = { id: string; direction: 'in' | 'out' };

function nameOnLetter(
  letter: ParsedBundleContent | null | undefined,
  sorId: string,
): string | null {
  for (const organization of letter?.organizations ?? []) {
    if (organization.sor?.trim() !== sorId) continue;
    const { name } = organizationFieldsFromStored(organization.name, organization.sor);
    if (name) return name;
  }
  return null;
}

/**
 * The organisation name the thread's MedCom letters give this SOR id.
 * Received letters come first (the name the other system wrote), in thread
 * order, then our own sent letters, then the opened letter. A label that is
 * only a SOR id is not a name.
 */
export function receiverNameFromThread(input: {
  sorId: string;
  messages: readonly ThreadMessage[];
  letters: ReadonlyMap<string, ParsedBundleContent | null | undefined>;
  opened?: ParsedBundleContent | null;
}): string | null {
  const sorId = input.sorId.trim();
  if (!sorId) return null;
  for (const direction of ['in', 'out'] as const) {
    for (const message of input.messages) {
      if (message.direction !== direction) continue;
      const name = nameOnLetter(input.letters.get(message.id), sorId);
      if (name) return name;
    }
  }
  return nameOnLetter(input.opened, sorId);
}

/**
 * Next receiver name for Videresend auto-fill, or undefined to leave the field.
 * Fills an empty field, and replaces a value that was itself auto-filled when the
 * SOR id changes. A name the user typed is never overwritten. A stale auto-filled
 * name is cleared when the new SOR id has no known name.
 */
export function nextAutoReceiverName(input: {
  current: string;
  lastAutoFilled: string | null;
  suggestion: string | null;
  loading: boolean;
}): string | undefined {
  const current = input.current.trim();
  const ownedByAutoFill = current === '' || current === input.lastAutoFilled;
  if (!ownedByAutoFill) return undefined;
  if (input.suggestion) {
    return input.suggestion === input.current ? undefined : input.suggestion;
  }
  if (input.loading || current === '') return undefined;
  return '';
}
